import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { CompanyService } from '../../../../services/company.service';

const HEX = /^#[0-9A-Fa-f]{6}$/;

interface Swatch {
  label: string;
  color: string;
}

/**
 * A colour: the native picker, the hex field, and one-click swatches for the
 * company's colours (main colour and text colour from "Marke & Branding") plus
 * black and white. Emits a lowercase `#rrggbb` whenever a valid colour is chosen.
 */
@Component({
  selector: 'app-color-field',
  standalone: true,
  imports: [HlmInputImports, HlmTooltipImports],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <div class="flex flex-wrap items-center gap-2">
      <input
        type="color"
        class="size-8 cursor-pointer rounded border-none bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-50"
        [attr.aria-label]="label() + ' wählen'"
        [disabled]="disabled()"
        [value]="value() ?? '#ffffff'"
        (input)="onInput($event)" />
      <input
        hlmInput
        [id]="id()"
        type="text"
        maxlength="7"
        class="w-24"
        [disabled]="disabled()"
        [value]="value() ?? ''"
        [attr.placeholder]="placeholder()"
        (change)="onInput($event)" />
      <span
        class="flex items-center gap-1"
        role="group"
        [attr.aria-label]="label() + ': Schnellwahl'">
        @for (swatch of swatches(); track swatch.label) {
          <button
            type="button"
            class="focus-visible:ring-ring size-6 rounded-full border border-black/20 shadow-sm transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            [class.ring-2]="isCurrent(swatch.color)"
            [class.ring-primary]="isCurrent(swatch.color)"
            [class.ring-offset-1]="isCurrent(swatch.color)"
            [style.background]="swatch.color"
            [disabled]="disabled()"
            [hlmTooltip]="swatch.label + ' ' + swatch.color"
            [attr.aria-label]="swatch.label + ' ' + swatch.color"
            [attr.aria-pressed]="isCurrent(swatch.color)"
            (click)="pick(swatch.color)"></button>
        }
      </span>
    </div>
  `,
})
export class ColorFieldComponent {
  private readonly companyService = inject(CompanyService);

  readonly id = input.required<string>();
  /** What the colour is, for the picker's accessible name ("Farbe", "Rahmenfarbe"). */
  readonly label = input('Farbe');
  readonly value = input<string | null>(null);
  readonly disabled = input(false);
  readonly placeholder = input<string | null>(null);
  readonly valueChange = output<string>();

  readonly swatches = computed<Swatch[]>(() => {
    const company = this.companyService.company();
    const own: Swatch[] = [
      { label: 'Hauptfarbe', color: company?.voucher_app_primary_color ?? '' },
      { label: 'Schriftfarbe', color: company?.voucher_app_text_color ?? '' },
    ];
    const fixed: Swatch[] = [
      { label: 'Schwarz', color: '#000000' },
      { label: 'Weiß', color: '#ffffff' },
    ];
    const seen = new Set<string>();
    return [...own, ...fixed]
      .map((s) => ({ ...s, color: s.color.toLowerCase() }))
      .filter((s) => HEX.test(s.color) && !seen.has(s.color) && seen.add(s.color));
  });

  isCurrent(color: string): boolean {
    return (this.value() ?? '').toLowerCase() === color;
  }

  onInput(event: Event) {
    this.pick((event.target as HTMLInputElement).value.trim());
  }

  pick(color: string) {
    if (!HEX.test(color)) return;
    const value = color.toLowerCase();
    if (value !== (this.value() ?? '').toLowerCase()) this.valueChange.emit(value);
  }
}
